import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import SelectionButton from '~/bootcamp/Activities/components/SelectionButton'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'

import { EquationPartsModel } from './PowerChoice.utils'
import usePowerChoice from './usePowerChoice'

/** Mirrors how the expression and options are rendered on screen. */
const partsToLatex = (parts: EquationPartsModel[]): string =>
  parts
    .map(({ exponent, integer, variable }, i) => {
      const sign = i > 0 || integer < 0 ? (integer < 0 ? ' - ' : ' + ') : ''
      const coefficient = !exponent || Math.abs(integer) > 1 ? `${Math.abs(integer)}` : ''
      const variablePart = exponent > 0 ? (exponent > 1 ? `${variable}^{${exponent}}` : variable) : ''
      return `${sign}${coefficient}${variablePart}`
    })
    .join('')

function PowerChoice({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const {
    answerIndex,
    options,
    equationParts,
    formValid,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
    clearForm,
  } = usePowerChoice({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })

  // Options in painted order; `isRight` stays out.
  useEmitInstance(
    onInstanceChange,
    equationParts && equationParts.length > 0 && options.length > 0
      ? `${partsToLatex(equationParts)}; options: ${options.map(({ equation }) => partsToLatex(equation)).join(', ')}`
      : null,
  )

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex flex-col">
        <div>
          {equationParts &&
            equationParts.map(({ exponent, integer, variable }, i) => (
              <span className="leading-10" key={i.toString()}>
                {(i > 0 || integer < 0) && ` ${integer < 0 ? '-' : '+'} `}
                {!exponent || Math.abs(integer) > 1 ? Math.abs(integer) : ''}
                {exponent > 0 && (
                  <>
                    <i>{variable}</i>
                    {exponent > 1 && <sup className="pl-1">{exponent}</sup>}
                  </>
                )}
              </span>
            ))}
        </div>
        <div className="flex flex-wrap justify-around mt-4">
          {options &&
            options.map(({ equation, isRight }, index) => (
              <div className="flex-1/2 p-2" key={index.toString()}>
                <SelectionButton
                  className="p-2 w-full h-24 flex justify-center items-center"
                  isSelected={answerIndex === index}
                  isWrong={answerState === false && answerIndex === index && !isRight}
                  onClick={onInputChange.bind(null, index)}
                >
                  <span>
                    {equation.map(({ exponent, integer, variable }, i) => (
                      <span className="leading-10" key={i.toString()}>
                        {(i > 0 || integer < 0) && ` ${integer < 0 ? '-' : '+'} `}
                        {!exponent || Math.abs(integer) > 1 ? Math.abs(integer) : ''}
                        {exponent > 0 && (
                          <>
                            <i>{variable}</i>
                            {exponent > 1 && <sup className="pl-1">{exponent}</sup>}
                          </>
                        )}
                      </span>
                    ))}
                  </span>
                </SelectionButton>
              </div>
            ))}
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: clearForm,
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default PowerChoice
